import React, { useState, useRef, useCallback, useEffect } from 'react';
import { fileToBase64, validateFile, isMobileDevice, isCameraAvailable, FileType } from '../utils/fileUtils';
import './ChatInput.css';

export interface FileAttachment {
  file: File;
  preview?: string;
  fileType: FileType;
  mimeType: string;
}

interface ChatInputProps {
  onSendMessage: (text: string) => void;
  onSendFile: (file: File, fileType: FileType, mimeType: string, base64: string) => void;
  disabled?: boolean;
  connectionStatus: 'connected' | 'disconnected';
  isMuted?: boolean;
  onToggleMute?: () => void;
}

function ChatInput({ onSendMessage, onSendFile, disabled, connectionStatus, isMuted, onToggleMute }: ChatInputProps) {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);

  // Check if mobile and camera available
  useEffect(() => {
    setIsMobile(isMobileDevice());
    isCameraAvailable().then(setCameraAvailable);
  }, []);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setError(null);
    const newAttachments: FileAttachment[] = [];
    const imagesToSend: FileAttachment[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validation = validateFile(file);

      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        continue;
      }

      const fileType = validation.fileType!;
      const mimeType = validation.mimeType!;

      // Create preview for images
      let preview: string | undefined;
      if (fileType === 'image') {
        preview = URL.createObjectURL(file);
      }

      const attachment: FileAttachment = {
        file,
        preview,
        fileType,
        mimeType,
      };

      newAttachments.push(attachment);

      // Auto-send images immediately
      if (fileType === 'image' && connectionStatus === 'connected' && !disabled) {
        imagesToSend.push(attachment);
      }
    }

    // Add attachments to UI (for preview)
    if (newAttachments.length > 0) {
      setAttachments((prev) => [...prev, ...newAttachments]);
    }

    // Auto-send images immediately
    for (const attachment of imagesToSend) {
      try {
        const base64 = await fileToBase64(attachment.file);
        onSendFile(attachment.file, attachment.fileType, attachment.mimeType, base64);
        
        // Remove from attachments after sending (but keep preview briefly so user can see it was uploaded)
        setTimeout(() => {
          setAttachments((prev) => {
            const index = prev.findIndex(att => att.file === attachment.file);
            if (index !== -1) {
              const newAtts = [...prev];
              if (newAtts[index].preview) {
                URL.revokeObjectURL(newAtts[index].preview!);
              }
              newAtts.splice(index, 1);
              return newAtts;
            }
            return prev;
          });
        }, 2000); // Remove after 2 seconds so user can see it was uploaded
      } catch (err) {
        setError(`Failed to send ${attachment.file.name}: ${err}`);
      }
    }
  }, [connectionStatus, disabled, onSendFile]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCameraInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input so same file can be selected again
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const newAttachments = [...prev];
      // Revoke object URL if it exists
      if (newAttachments[index].preview) {
        URL.revokeObjectURL(newAttachments[index].preview!);
      }
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleSend = useCallback(async () => {
    if (disabled || connectionStatus !== 'connected') {
      return;
    }

    setError(null);

    // Send text message if there's text
    if (text.trim()) {
      onSendMessage(text.trim());
      setText('');
    }

    // Send non-image attachments (images are auto-sent on upload)
    const attachmentsToSend = attachments.filter(att => att.fileType !== 'image');
    for (const attachment of attachmentsToSend) {
      try {
        const base64 = await fileToBase64(attachment.file);
        onSendFile(attachment.file, attachment.fileType, attachment.mimeType, base64);
      } catch (err) {
        setError(`Failed to process ${attachment.file.name}: ${err}`);
        continue;
      }
    }

    // Clear non-image attachments after sending
    attachmentsToSend.forEach((att) => {
      if (att.preview) {
        URL.revokeObjectURL(att.preview);
      }
    });
    setAttachments((prev) => prev.filter(att => att.fileType === 'image'));
  }, [text, attachments, onSendMessage, onSendFile, disabled, connectionStatus]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      attachments.forEach((att) => {
        if (att.preview) {
          URL.revokeObjectURL(att.preview);
        }
      });
    };
  }, [attachments]);

  return (
    <div className="chat-input-container">
      {error && (
        <div className="chat-input-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="chat-input-attachments">
          {attachments.map((att, index) => (
            <div key={index} className="chat-input-attachment">
              {att.preview ? (
                <img src={att.preview} alt={att.file.name} />
              ) : (
                <div className="chat-input-attachment-icon">
                  {att.fileType === 'audio' && '🎵'}
                  {att.fileType === 'document' && '📄'}
                </div>
              )}
              <span className="chat-input-attachment-name">{att.file.name}</span>
              <button
                className="chat-input-attachment-remove"
                onClick={() => removeAttachment(index)}
                aria-label="Remove attachment"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className={`chat-input-wrapper ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="chat-input-controls">
          <button
            className="chat-input-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || connectionStatus !== 'connected'}
            aria-label="Upload file"
            title="Upload file"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
            </svg>
          </button>

          {isMobile && cameraAvailable && (
            <button
              className="chat-input-button chat-input-camera-button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={disabled || connectionStatus !== 'connected'}
              aria-label="Take photo"
              title="Take photo"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 6h-3.17l-1.24-1.35c-.37-.41-.91-.65-1.47-.65H8.88c-.56 0-1.1.24-1.47.65L6.17 6H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 14H3V8h3.17l1.24 1.35c.37.41.91.65 1.47.65h6.24c.56 0 1.1-.24 1.47-.65L17.83 8H21v12zm-9-7c-1.65 0-3 1.35-3 3s1.35 3 3 3 3-1.35 3-3-1.35-3-3-3z"/>
              </svg>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
            accept="image/*,audio/*,.txt,.md,.pdf,.docx,.xlsx,.csv,.pptx"
            multiple
          />

          {isMobile && cameraAvailable && (
            <input
              ref={cameraInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleCameraInputChange}
              accept="image/*"
              capture="environment"
            />
          )}

          <textarea
            ref={textInputRef}
            className="chat-input-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={connectionStatus === 'connected' ? "Type..." : "Connect to start chatting"}
            disabled={disabled || connectionStatus !== 'connected'}
            rows={1}
          />

          {onToggleMute && (
            <button
              className={`chat-input-mute-button ${isMuted ? 'muted' : ''}`}
              onClick={onToggleMute}
              aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
            >
              {isMuted ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                </svg>
              )}
            </button>
          )}

          <button
            className="chat-input-send-button"
            onClick={handleSend}
            disabled={disabled || connectionStatus !== 'connected' || (!text.trim() && attachments.filter(att => att.fileType !== 'image').length === 0)}
            aria-label="Send message"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatInput;
